import { supabase } from './supabase.ts';
import type {
  Decision,
  Experiment,
  Funnel,
  Note,
  Recommendation,
  TrackedEvent,
  TrackedLink,
} from '../../netlify/functions/_lib/domain.ts';

async function token(): Promise<string> {
  if (!supabase) throw new Error('Supabase is not configured.');
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) throw new Error('Sign in to continue.');
  return data.session.access_token;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${await token()}`);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const res = await fetch(path, { ...init, headers });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string } & T;
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `Request failed (${res.status})`);
  }
  return body;
}

export type ExperimentSummary = {
  experiment: Experiment;
  funnel: Funnel;
  recommendation: Recommendation;
  events: TrackedEvent[];
  notes: Note[];
  links: TrackedLink[];
  decisions: Decision[];
};

export const apiClient = {
  listExperiments: () => api<{ experiments: Experiment[] }>('/api/experiments'),
  createExperiment: (body: Record<string, unknown>) =>
    api<{ experiment: Experiment }>('/api/experiments', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getExperiment: (id: string) => api<{ experiment: Experiment }>(`/api/experiments/${id}`),
  updateExperiment: (id: string, body: Record<string, unknown>) =>
    api<{ experiment: Experiment }>(`/api/experiments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteExperiment: (id: string) =>
    api<{ ok: true }>(`/api/experiments/${id}`, { method: 'DELETE' }),
  summary: (id: string) => api<ExperimentSummary>(`/api/experiments/${id}/summary`),
  addEvent: (id: string, body: Record<string, unknown>) =>
    api<{ event: TrackedEvent }>(`/api/experiments/${id}/events`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteEvent: (id: string) => api<{ ok: true }>(`/api/events/${id}`, { method: 'DELETE' }),
  addNote: (id: string, body: string) =>
    api<{ note: Note }>(`/api/experiments/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  deleteNote: (id: string) => api<{ ok: true }>(`/api/notes/${id}`, { method: 'DELETE' }),
  addLink: (id: string, body: Record<string, unknown>) =>
    api<{ link: TrackedLink }>(`/api/experiments/${id}/links`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  deleteLink: (id: string) => api<{ ok: true }>(`/api/links/${id}`, { method: 'DELETE' }),
  saveDecision: (id: string, body: Record<string, unknown>) =>
    api<{ decision: Decision }>(`/api/experiments/${id}/decisions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
